// Single source of truth for which access tier can see which web page.
// Read by both RequireAuth (route guard) and Sidebar (nav filtering) — never
// duplicate this mapping elsewhere.
//
// Each tier is a strict superset of the one below it. Rules are checked
// longest-path-first (exact match, or "starts with path + '/'"), so a specific
// path like /team/add is matched before the general /team prefix rule.
const RULES = [
  // Overview
  { path: '/dashboard', tiers: ['Incident Commander', 'Admin'] },
  { path: '/command', tiers: ['Incident Commander', 'Admin'] },
  { path: '/executive', tiers: ['Incident Commander', 'Admin'] },
  { path: '/team-leader', tiers: ['Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/entry-control', tiers: ['Entry Control Officer', 'Team Leader', 'Incident Commander', 'Admin'] },

  // Operations
  { path: '/activation/announce', tiers: ['Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/activation/simulate', tiers: ['Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/activation/drills', tiers: ['Medical Staff', 'Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/activation', tiers: ['Medical Staff', 'Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/active-incident', tiers: ['Medical Staff', 'Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/closed-incident', tiers: ['Medical Staff', 'Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/team/add', tiers: ['Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/team/members', tiers: ['Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/team', tiers: ['Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/hospitals', tiers: ['Medical Staff', 'Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/staff-app', tiers: ['Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/patient-app', tiers: ['Medical Staff', 'Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/patient', tiers: ['Medical Staff', 'Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/board', tiers: ['Medical Staff', 'Team Leader', 'Incident Commander', 'Admin'] },
  { path: '/stakeholders', tiers: ['Incident Commander', 'Admin'] },
  { path: '/inventory', tiers: ['Team Leader', 'Incident Commander', 'Admin'] },

  // System — admin-only
  { path: '/reports', tiers: ['Incident Commander', 'Admin'] },
  { path: '/users', tiers: ['Admin'] },
  { path: '/audit', tiers: ['Admin'] },
  { path: '/integrations', tiers: ['Admin'] },
  { path: '/governance', tiers: ['Admin'] },
  { path: '/settings', tiers: ['Admin'] },
].sort((a, b) => b.path.length - a.path.length);

// A tier missing entirely (stale localStorage from before this feature shipped, or an
// unrecognized/new path) is treated as allowed — fail open rather than break navigation.
//
// High Management sits above Admin and can see every page, including admin-only ones —
// Phase 1 of that role is view access only. It does NOT yet make anything read-only:
// every write action (forms, buttons, API calls) still behaves exactly as it does for
// whichever tier would otherwise see that page. Enforcing actual read-only behavior
// (disabling writes client-side AND rejecting them server-side) is a separate, larger
// follow-up — don't assume this tier is safe to hand out as if it can't make changes.
// The pages an incident's designated team leader can open whatever their access tier —
// the server lets them run that incident's roster (rosterWriteGuard), so the console
// they do it from has to be reachable. `user.leadsIncident` comes from sign-in / auth/me.
const INCIDENT_LEADER_PATHS = ['/team-leader'];

export function isPathAllowed(pathname, tier, user) {
  if (user?.leadsIncident && INCIDENT_LEADER_PATHS.includes(pathname)) return true;
  if (!tier) return true;
  if (tier === 'High Management') return true;
  const rule = RULES.find((r) => pathname === r.path || pathname.startsWith(r.path + '/'));
  // Entry Control Officer is a single-purpose tier — fail closed so it only ever sees
  // pages explicitly granted to it, never an unlisted page by default.
  if (tier === 'Entry Control Officer') return !!rule && rule.tiers.includes(tier);
  if (!rule) return true;
  return rule.tiers.includes(tier);
}

// Where to send a tier when it hits a page it can't see.
export const TIER_HOME = {
  'Medical Staff': '/activation',
  'Entry Control Officer': '/entry-control',
  'Team Leader': '/team-leader',
  'Incident Commander': '/dashboard',
  Admin: '/dashboard',
  'High Management': '/dashboard',
};

export const ALL_TIERS = ['Medical Staff', 'Entry Control Officer', 'Team Leader', 'Incident Commander', 'Admin', 'High Management'];
